import React, { useState } from 'react';
import { CreateQuestionDto, QuestionType, Difficulty, QuestionOption } from '../../types/exam';

interface QuestionFormProps {
  onSubmit: (data: CreateQuestionDto | any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  isEditing?: boolean;
}

const QUESTION_TYPES: QuestionType[] = ['MCQ', 'SHORT_ANSWER', 'ESSAY', 'TRUE_FALSE', 'MATCHING'];
const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];

type QuestionFormData = CreateQuestionDto & {
  correctAnswer?: string;
};

export const QuestionForm: React.FC<QuestionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState<QuestionFormData>({
    questionText: initialData?.questionText || '',
    questionType: initialData?.questionType || 'MCQ',
    chapter: initialData?.chapter || '',
    marks: initialData?.marks || 1,
    negativeMarks: initialData?.negativeMarks || 0,
    difficulty: initialData?.difficulty || 'MEDIUM',
    options: initialData?.options || [],
    correctAnswer: initialData?.correctAnswer || '',
    explanation: initialData?.explanation || '',
    sequenceNumber: initialData?.sequenceNumber,
    tags: initialData?.tags || [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleAddOption = () => {
    const newOption: QuestionOption = {
      id: `opt_${Date.now()}`,
      text: '',
      isCorrect: false,
    };
    setFormData((prev) => ({
      ...prev,
      options: [...(prev.options || []), newOption],
    }));
  };

  const handleUpdateOption = (
    index: number,
    field: keyof QuestionOption,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      options: (prev.options || []).map((opt, idx) =>
        idx === index ? { ...opt, [field]: value } : opt
      ),
    }));
  };

  const handleRemoveOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: (prev.options || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((t) => t !== tag),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.questionText.trim()) {
      setError('Question text is required');
      return;
    }

    if (formData.questionType === 'MCQ' && (formData.options?.length || 0) < 2) {
      setError('MCQ questions must have at least 2 options');
      return;
    }

    if (
      formData.questionType === 'MCQ' &&
      !formData.options?.some((opt) => opt.isCorrect)
    ) {
      setError('At least one option must be marked as correct');
      return;
    }

    if (formData.marks <= 0) {
      setError('Marks must be greater than 0');
      return;
    }

    if (formData.negativeMarks && formData.negativeMarks < 0) {
      setError('Negative marks cannot be negative');
      return;
    }

    try {
      setLoading(true);
      const payload: CreateQuestionDto = {
        questionText: formData.questionText.trim(),
        questionType: formData.questionType,
        chapter: formData.chapter?.trim() || '',
        marks: formData.marks,
        negativeMarks: formData.negativeMarks || 0,
        difficulty: formData.difficulty,
        sequenceNumber:
          isEditing && typeof formData.sequenceNumber === 'number'
            ? formData.sequenceNumber
            : undefined,
        explanation: formData.explanation?.trim() || '',
        tags: formData.tags || [],
        options:
          formData.questionType === 'MCQ'
            ? (formData.options || []).map((opt) => ({
                text: opt.text.trim(),
                isCorrect: !!opt.isCorrect,
              }))
            : undefined,
      };

      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 space-y-6 border border-gray-200">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Question Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question Text *
        </label>
        <textarea
          name="questionText"
          value={formData.questionText}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter the question text"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Question Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question Type *
          </label>
          <select
            name="questionType"
            value={formData.questionType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {QUESTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Marks */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marks *
          </label>
          <input
            type="number"
            name="marks"
            value={formData.marks}
            onChange={handleChange}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Negative Marks
          </label>
          <input
            type="number"
            name="negativeMarks"
            value={formData.negativeMarks || 0}
            onChange={handleChange}
            min="0"
            step="0.25"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Difficulty *
          </label>
          <select
            name="difficulty"
            value={formData.difficulty}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {DIFFICULTIES.map((diff) => (
              <option key={diff} value={diff}>
                {diff}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chapter
        </label>
        <input
          type="text"
          name="chapter"
          value={formData.chapter || ''}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Example: Algebra"
        />
      </div>

      {/* MCQ Options */}
      {formData.questionType === 'MCQ' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Options
            </label>
            <button
              type="button"
              onClick={handleAddOption}
              className="text-blue-500 hover:text-blue-700 text-sm font-medium"
            >
              + Add Option
            </button>
          </div>

          <div className="space-y-2">
            {(formData.options || []).map((option, idx) => (
              <div key={option.id || `opt-${idx}`} className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={option.isCorrect}
                  onChange={(e) => handleUpdateOption(idx, 'isCorrect', e.target.checked)}
                  className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => handleUpdateOption(idx, 'text', e.target.value)}
                  placeholder="Enter option text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveOption(idx)}
                  className="text-red-500 hover:text-red-700 px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Correct Answer for non-MCQ */}
      {formData.questionType !== 'MCQ' && formData.questionType !== 'TRUE_FALSE' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Correct Answer
          </label>
          <textarea
            name="correctAnswer"
            value={formData.correctAnswer}
            onChange={handleChange}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter the correct answer (for teacher reference)"
          />
        </div>
      )}

      {/* Explanation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Explanation (optional)
        </label>
        <textarea
          name="explanation"
          value={formData.explanation}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter explanation for the answer"
        />
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags (optional)
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            placeholder="Add tag and press Enter"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Add
          </button>
        </div>
        {formData.tags && formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag) => (
              <span
                key={tag}
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="text-blue-600 hover:text-blue-800 font-bold"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-2 rounded font-medium transition"
        >
          {loading ? 'Saving...' : `${isEditing ? 'Update' : 'Add'} Question`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-6 py-2 rounded font-medium transition"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default QuestionForm;
